import cn from 'classnames';
import React, { useState } from 'react';
import { useAtomValue } from 'jotai/utils';
import BaseModal from 'src/components/BaseModal';
import { BaseModalProps } from 'src/components/BaseModal/BaseModal';
import { Loader } from 'src/components';
import { useAdminUserSettingsQuery } from 'src/api/queries/adminUserSettingsQueries';
import { settingsAtom } from 'src/shared/settings/index';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import AccountTab from './tabs/AccountTab';
import AddonsTab from './tabs/AddonsTab';
import AiBudgetTab from './tabs/AiBudgetTab';
import WatermarkTab from './tabs/WatermarkTab';
import ImageTab from './tabs/ImageTab';
import VideoTab from './tabs/VideoTab';
import WebhookTab from './tabs/WebhookTab';
import DatabaseTab from './tabs/DatabaseTab';

import styles from './styles.module.scss';

type TabId = 'account' | 'addons' | 'ai-budget' | 'watermark' | 'image' | 'video' | 'webhook' | 'database';

const BASE_TABS: { id: TabId; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'addons', label: 'Addons' },
  { id: 'watermark', label: 'Watermark' },
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
  { id: 'webhook', label: 'Webhook' },
  { id: 'database', label: 'Database' },
];

interface UserSettingsModalProps extends BaseModalProps {
  user: any;
  onEditUser?: () => void;
}

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({ user, onEditUser, ...rest }) => {
  const [activeTab, setActiveTab] = useState<TabId>('account');

  useSettingsQuery();
  const adminSettings = useAtomValue(settingsAtom);

  const { data: userSettings, isLoading, error } = useAdminUserSettingsQuery(user?.id);

  const hasAiAddon = userSettings?.addons?.AI_ASSISTANT?.added === true;

  const tabs = React.useMemo(() => {
    if (!hasAiAddon) return BASE_TABS;
    const aiBudgetTab = { id: 'ai-budget' as TabId, label: 'AI Budget' };
    const addonsIdx = BASE_TABS.findIndex((t) => t.id === 'addons');
    const result = [...BASE_TABS];
    result.splice(addonsIdx + 1, 0, aiBudgetTab);
    return result;
  }, [hasAiAddon]);

  const renderTabContent = () => {
    if (activeTab === 'account') {
      return <AccountTab user={user} onEditUser={onEditUser} />;
    }

    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorBanner}>
          Failed to load user settings. The user may not be in your group.
        </div>
      );
    }

    if (!userSettings) {
      return <div className={styles.emptyState}>No settings data available.</div>;
    }

    switch (activeTab) {
      case 'addons':
        return <AddonsTab userId={user.id} userSettings={userSettings} adminSettings={adminSettings} />;
      case 'ai-budget':
        return <AiBudgetTab userId={user.id} />;
      case 'watermark':
        return <WatermarkTab userId={user.id} userSettings={userSettings} />;
      case 'image':
        return <ImageTab userId={user.id} userSettings={userSettings} />;
      case 'video':
        return <VideoTab userId={user.id} userSettings={userSettings} adminSettings={adminSettings} />;
      case 'webhook':
        return <WebhookTab userId={user.id} userSettings={userSettings} />;
      case 'database':
        return <DatabaseTab userId={user.id} userSettings={userSettings} />;
      default:
        return null;
    }
  };

  return (
    <BaseModal {...rest} containerStyle={{ maxWidth: 960, width: '92vw' }}>
      <div className={styles.modalHeader}>
        <h3>Settings for {user?.email}</h3>
        <p>
          {user?.first_name} {user?.last_name} &middot; {user?.role_name}
        </p>
      </div>

      <div className={styles.tabSwitcher}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={cn(styles.tab, { [styles.tabActive]: activeTab === tab.id })}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>{renderTabContent()}</div>
    </BaseModal>
  );
};

export default UserSettingsModal;
